import BugsnagPerformance from '@bugsnag/browser-performance';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { ReactRouterRoutingProvider } from '@bugsnag/react-router-performance';
import { Labels } from '@lib/sc-eone-ui/src/strings/strings-en-us';
import { ErrorPanel } from 'eone-ui/panels/error-panel';
import type { ReactNode } from 'react';
import React, { useEffect, useRef } from 'react';
import type { RouteObject } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';

let BugsnagErrorBoundary;

function initBugsnag() {
  if (import.meta.env.VITE_BUGSNAG_API_KEY) {
    Bugsnag.start({
      apiKey: import.meta.env.VITE_BUGSNAG_API_KEY,
      plugins: [new BugsnagPluginReact()],
      onError: (event) => {
        if (window.__version) {
          event.app.version = window.__version;
        }
        if (window.__userSession) {
          event.setUser(
            window.__userSession.sessionId,
            window.__userSession.email,
            window.__userSession.accountName,
          );
        }

        event.addMetadata('git', { commitHash: __COMMIT_HASH__ });
      },
    });

    BugsnagErrorBoundary = Bugsnag.getPlugin('react')!.createErrorBoundary(React);
  }
}

function initBugsnagPerformance(routes: RouteObject[]) {
  if (import.meta.env.VITE_BUGSNAG_API_KEY) {
    BugsnagPerformance.start({
      // samplingProbability: 1,
      apiKey: import.meta.env.VITE_BUGSNAG_API_KEY,
      routingProvider: new ReactRouterRoutingProvider(routes),
      onSpanEnd: [
        (span) => {
          if (window.__version) {
            span.setAttribute('app.version', window.__version);
          }

          if (window.__userSession) {
            span.setAttribute('user.id', window.__userSession.sessionId);
            span.setAttribute('user.name', window.__userSession.accountName);
            span.setAttribute('user.email', window.__userSession.email);
          }

          span.setAttribute('git.commitHash', __COMMIT_HASH__);

          return true;
        },
      ],
    });
  }
}

const FallbackComponent = (props) => {
  const navigate = useNavigate();

  const getMessage = (error) => {
    if (import.meta.env.MODE === 'development') {
      return error?.message || 'Something went wrong';
    }
    return 'Something went wrong';
  };

  const getPrompts = () => {
    const prompts: { Label: string; OnClick: () => void }[] = [];

    prompts.push({
      Label: 'Click here to return to your home page.',
      OnClick: () => navigate('/'),
    });

    prompts.push({
      Label: 'Click here to reload the page',
      OnClick: () => window.location.reload(),
    });

    return prompts;
  };

  // Bugfix: navigate will not work directly because react-router Outlet has been unmounted
  // and replaced by this fallback component
  // Capture and handle browser loction change properly in error page
  const location = useLocation();
  const refPathname = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== refPathname.current) {
      window.location.reload();
    }
  }, [location.pathname]);

  return (
    <ErrorPanel Title={Labels.Error} Message={getMessage(props.error)} Prompts={getPrompts()} />
  );
};

const ErrorBoundary = ({ children }: { children: ReactNode }) => {
  if (BugsnagErrorBoundary) {
    return (
      <BugsnagErrorBoundary FallbackComponent={FallbackComponent}>{children}</BugsnagErrorBoundary>
    );
  } else {
    console.warn('Bugsnag is not initialized');
    return <>{children}</>;
  }
};

export { ErrorBoundary, initBugsnag, initBugsnagPerformance };
